import React, { useState, useEffect } from 'react';
import Textarea from '@amzn/meridian/textarea';
import Text from '@amzn/meridian/text';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import PropTypes from 'prop-types';
import { CgAsterisk } from 'react-icons/cg';
import useResponse from '../hooks/useResponse';
import { PLACEHOLDERS } from '../constants/FeedbackConstants';
import colorStyles from '../enums/colorStyles';

/**
 * TextQuestion component will be used to get users suggestions.
 * @param {string} question - Question text
 * @param {number} [limit = 60] - Maximum number of words a user can enter
 * @param {string} [label = ""]
 * @param {string} [placeholder = "Enter your valuable suggestions"]
 * @param {number} [rows = 4] - Used to define width of Textarea
 * @returns Meridian Textarea Component
 */

const TextQuestion = ({
  question,
  id,
  isMandatory,
  limit,
  label,
  placeholder,
  rows,
  prePopulatedValue,
}) => {
  const [content, setContent] = useState('');
  const [charCount, setCharCount] = useState(0);
  const {
    saveFeedbackResponse,
    response: { answers },
  } = useResponse();

  const limitCharacters = (text) => {
    let response;
    const trimmedText = text?.trim();
    if (trimmedText.length === 0) {
      response = '';
      setContent(trimmedText);
    } else if (trimmedText?.length > limit) {
      const updatedText = trimmedText.slice(0, limit);
      setContent(updatedText);
      setCharCount(limit);
      response = updatedText;
    } else {
      setContent(text);
      setCharCount(trimmedText.length);
      response = trimmedText;
    }
    saveFeedbackResponse({ id, answer: response });
  };

  useEffect(() => {
    if (prePopulatedValue) {
      limitCharacters(prePopulatedValue);
    }
  }, [prePopulatedValue, answers.length]);

  return (
    <Column spacingInset="xxsmall">
      <Text>
        {question}{' '}
        {isMandatory && (
          <CgAsterisk aria-label="required" color={colorStyles.REQUIRED} />
        )}
      </Text>
      <Textarea
        rows={rows}
        value={content}
        onChange={(value) => limitCharacters(value)}
        placeholder={placeholder}
        label={label}
        disabled={Boolean(prePopulatedValue)}
      />
      <Row alignmentHorizontal="right" widths="fit">
        <Text>
          {charCount}/{limit}
        </Text>
      </Row>
    </Column>
  );
};

TextQuestion.propTypes = {
  question: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  isMandatory: PropTypes.bool.isRequired,
  limit: PropTypes.number,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  rows: PropTypes.number,
  prePopulatedValue: PropTypes.string,
};

TextQuestion.defaultProps = {
  limit: 60,
  label: '',
  placeholder: PLACEHOLDERS.textQuestion,
  rows: 4,
  prePopulatedValue: '',
};

export default TextQuestion;
